import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon, ChevronDownIcon, PhoneIcon, XIcon } from 'lucide-react';
import { ButtonLink } from '../ButtonLink';
import { primaryNavLinks, serviceMenuItems } from '../../data/navigation';
import { contact, images, urls } from '../../data/site';
import { linkTargetProps } from '../../utils/links';
interface MobileMenuProps {
  open: boolean;
  onClose: () => void;
  onNavigate: (e: React.MouseEvent<HTMLAnchorElement>, href: string) => void;
}
export function MobileMenu({
  open,
  onClose,
  onNavigate
}: MobileMenuProps) {
  const reduce = useReducedMotion();
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const [servicesOpen, setServicesOpen] = useState(false);
  useEffect(() => {
    if (open) closeRef.current?.focus();else setServicesOpen(false);
  }, [open]);
  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'Tab' || !panelRef.current) return;
    const focusables = panelRef.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled])');
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
  return <AnimatePresence data-id="element-176" data-mp="b6rd9coo5bn">
      {open && <motion.div id="mobile-menu" ref={panelRef} role="dialog" aria-modal="true" aria-label="Site menu" onKeyDown={handleKeyDown} initial={{
      opacity: 0,
      y: reduce ? 0 : -8
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: reduce ? 0 : -6
    }} transition={{
      duration: 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} className="fixed inset-0 z-50 flex flex-col overflow-y-auto bg-paper lg:hidden" data-id="element-177" data-mp="4csa3bbwfdtr">
          <div className="flex h-[72px] shrink-0 items-center justify-between border-b border-line px-5 sm:px-8" data-id="element-178" data-mp="eza9gwtvndie">
            <img src={images.logo} alt="Motor City Truck Collision" className="h-10 w-auto max-w-[190px] object-contain" data-id="element-179" data-mp="tmn2f6m0klit" />
            <button ref={closeRef} type="button" onClick={onClose} aria-label="Close menu" className="inline-flex h-11 w-11 items-center justify-center border border-line text-ink" data-id="element-180" data-mp="op3knwq3xqm4">
              <XIcon aria-hidden="true" className="h-5 w-5" data-id="element-181" data-mp="9zxpqkyaymk1" />
            </button>
          </div>

          <nav aria-label="Mobile" className="flex-1 px-5 py-4 sm:px-8" data-id="element-182" data-mp="5xubxpo5v5vl">
            <ul data-id="element-183" data-mp="1leuk8ad9a3ys">
              <li className="border-b border-line" data-id="element-184" data-mp="1leb7r96dk7s5">
                <button type="button" aria-expanded={servicesOpen} aria-controls="mobile-services" onClick={() => setServicesOpen(v => !v)} className="flex min-h-[60px] w-full items-center justify-between font-display text-[30px] font-semibold uppercase text-ink" data-id="element-185" data-mp="1lgxifhokb3el">
                  Services
                  <ChevronDownIcon aria-hidden="true" className={`h-6 w-6 transition-transform duration-200 ease-out ${servicesOpen ? 'rotate-180 text-brand' : ''}`} data-id="element-186" data-mp="1f0ozilkkd6pj" />
                </button>
                {servicesOpen && <ul id="mobile-services" className="pb-4" data-id="element-187" data-mp="2clkxlnw1fd73">
                    {serviceMenuItems.map(item => <li key={item.label} data-id="element-188" data-mp="1xvm25svvafvb">
                        <a href={item.href} onClick={e => onNavigate(e, item.href)} {...linkTargetProps(item.href)} className="flex min-h-[48px] flex-col justify-center border-l-2 border-line py-2 pl-4 hover:border-brand" data-id="element-189" data-mp="22vrpwxz0ni84">
                          <span className="text-[17px] font-semibold text-ink" data-id="element-190" data-mp="17m67cyg8swx8">{item.label}</span>
                          <span className="text-sm text-steel" data-id="element-191" data-mp="12rsizfzzzq22">{item.description}</span>
                        </a>
                      </li>)}
                  </ul>}
              </li>
              {primaryNavLinks.map(link => <li key={link.href} className="border-b border-line" data-id="element-192" data-mp="1qeh4scs4o2ny">
                  <a href={link.href} onClick={e => onNavigate(e, link.href)} className="flex min-h-[60px] items-center justify-between font-display text-[30px] font-semibold uppercase text-ink" data-id="element-193" data-mp="1lfnqii50o7mq">
                    {link.label}
                    <ArrowRightIcon aria-hidden="true" className="h-5 w-5 text-steel" data-id="element-194" data-mp="296jmlrgi9ozs" />
                  </a>
                </li>)}
            </ul>
          </nav>

          <div className="grid gap-3 border-t border-line bg-paper-deep px-5 py-6 sm:px-8" data-id="element-195" data-mp="2cnxlpqp3rzyx">
            <ButtonLink href={urls.quote} trailingIcon={ArrowRightIcon} className="w-full" data-id="element-196" data-mp="2goreaxdhq2y5">
              Request a Quote
            </ButtonLink>
            <ButtonLink href={contact.phoneHref} variant="outlineLight" leadingIcon={PhoneIcon} className="w-full" data-id="element-197" data-mp="2lmj49lukrezp">
              Call {contact.phoneDisplay}
            </ButtonLink>
          </div>
        </motion.div>}
    </AnimatePresence>;
}